<template>
  <div
    :key="`product_${productData?.product?.id}_${key}`"
    class="product"
  >
    <template v-if="productData?.sections && productData?.product">
      <template
        v-for="widget, i in confProduct.widgets"
      >
        <LazyWidgetsSystemBreadcrumbs
          v-if="widget.widgetId === 'Breadcrumbs' && widget.widgetEnable"
          :key="widget.widgetId + i"
          :is-head="true"
          :page-data="productData.product"
          :data="assignWidgetOptions(widget.data, {})"
        />

        <LazyWidgets
          v-if="productData.sections.find(n => n.name === widget.sectionId) || widget.data"
          :id="widget.widgetId"
          :key="widget.widgetId + i"
          :widget-enable="widget.widgetEnable"
          :widget-default-fields-enable="widget.widgetDefaultFieldsEnable"
          :data="productData.sections.find(n => n.name === widget.sectionId) ? assignWidgetOptions(widget.data, productData.sections.find(n => n.name === widget.sectionId).data) : widget.data"
          :section-data="productData.sections.find(n => n.name === widget.sectionId) ? productData.sections.find(n => n.name === widget.sectionId).data : {}"
          :product-data="productData.product"
          :product-spec-data="productSpecData"
          :options="{
            addonsInOrder,
            settingsData: productData.settings,
            globalData: productData.global
          }"
          @return-for-gtm="e => {
            emit('returnForGtm', e)
          }"
        />
      </template>

      <ClientOnly
        v-if="headerRef && confProduct.showProductLogoOnHeader && layout === 'product' && productData.product.mainImage.original && productLogoRef"
      >
        <Teleport
          to="#productLogo"
        >
          <div class="onlyFixedBlock productInfo hide-on-small-and-down">
            <div class="productInfo__logo">
              <nuxt-picture
                loading="lazy"
                :img-attrs="{ alt: productData.product.heading }"
                :src="productData.product.mainImage.original"
                sizes="sm:100vw"
                height="50"
                width="91"
              />
            </div>
            <span class="h4 productInfo__title">
              {{ productData.product.heading }}
            </span>
          </div>
        </Teleport>
      </ClientOnly>

      <ClientOnly
        v-if="headerRef && confProduct.showBtnOnHeader && layout === 'product'"
        :set="canCustomize = (productData.settings.can_design || productData.settings.can_edit) && !productData.settings.is_quote_only &&
          (confProduct.widgets.find(n => n.widgetId === 'ProductsHero').data.actionBtns.customize && confProduct.widgets.find(n => n.widgetId === 'ProductsHero').data.actionBtns.customize.enable)"
      >
        <Teleport to="#customizeNow">
          <div
            v-if="canCustomize"
            class="btn-holder"
          >
            <NuxtLink
              :to="`/designer?product-id=${productData.product.id}${addonsInOrder ? '&addons=' + addonsInOrder.join(',') : ''}`"
              class="btn orange waves-effect waves-light"
            >
              {{ confProduct.widgets.find(n => n.widgetId === 'ProductsHero').data.actionBtns.customize.buttonTitle }}
            </NuxtLink>
          </div>

          <div
            v-else-if="confProduct.widgets.find(n => n.widgetId === 'ProductsHero').data.actionBtns.quote && confProduct.widgets.find(n => n.widgetId === 'ProductsHero').data.actionBtns.quote.enable"
            class="btn-holder"
          >
            <NuxtLink
              :to="`/designer?product-id=${productData.product.id}&quote=true${addonsInOrder ?? '&addons=' + addonsInOrder.join(',')}`"
              class="btn waves-effect waves-light quoteBtn"
              :class="confProduct.widgets.find(n => n.widgetId === 'ProductsHero').data.actionBtns.customize.buttonClasses"
            >
              {{ confProduct.widgets.find(n => n.widgetId === 'ProductsHero').data.actionBtns.quote.buttonTitle }}
            </NuxtLink>
          </div>
        </Teleport>
      </ClientOnly>
    </template>
    <template v-else-if="productData?.error?.code === 404">
      <PagesNotFound />
    </template>
  </div>
</template>

<script setup>
const { params } = useRoute()
const { assignWidgetOptions } = useWidgets()
const { layout, headerRef } = useHelpers()
const { getBreadcrumbs } = useBreadcrumbs()
const { confGlobal, confMain } = useConfig()
const { trackEventQueue } = useCustomTools()
const $dayjs = useDayjs()

const key = ref(1)
const addonsInOrder = ref([])
const productLogoRef = ref(false)

/**
* Emits
*/
const emit = defineEmits(['returnForGtm'])

/**
* Get Product data
*/
const [productDataRes, productSpecRes] = await Promise.all([
  useGetProduct(params?.slugId),
  useGetProductSpec(params?.slugId)
])

const { data: productData, error } = productDataRes
const { data: productSpecData } = productSpecRes

if (error.value || !productData.value) {
  throw createError(error.value)
}

/**
* Get Breadcrumbs
*/
getBreadcrumbs(productData.value, 'product')

/**
* Config with product data for conversation form
*/
const { confProduct } = useConfig({
  name: productData.value?.product?.heading,
  category: productData.value?.product?.breadcrumbs[productData.value?.product?.breadcrumbs.length - 2]?.title
})

/**
* Meta Tags
*/
useHead({
  title: productData.value?.product?.heading,
  meta: [{
    name: 'description',
    content: productData.value?.product?.productmetadescription
  },
  {
    name: 'keywords',
    content: productData.value?.product?.productkeywords
  }]
})

/**
* Json Schema
*/
useHead({
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'http://schema.org',
        '@type': 'Product',
        name: productData.value?.product?.heading ? productData.value?.product?.heading : productData.value?.product?.producttitle,
        'description': productData.value?.product?.heading ? productData.value?.product?.heading : productData.value?.product?.producttitle,
        image: productData.value?.product?.mainImage.original,
        brand: {
          '@type': 'Brand',
          name: confMain.siteName
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          bestRating: '5',
          ratingCount: '2500'
        },
        offers: {
          '@type': 'Offer',
          url: `${confGlobal.currentSiteUrl}/product/${productData.value?.product?.producturlpathname}-${productData.value?.product?.id}`,
          priceCurrency: 'USD',
          price: productData.value?.product?.pre_populated_unit_price,
          priceValidUntil: $dayjs(new Date()).format('YYYY-MM-DD').toString(),
          itemCondition: 'http://schema.org/NewCondition',
          availability: 'http://schema.org/InStock'
        },
        'hasMerchantReturnPolicy': {
          '@type': 'MerchantReturnPolicy',
          'returnPolicyCategory': 'https://schema.org/ReturnNotPermitted'
        },
        'shippingDetails': {
          '@type': 'OfferShippingDetails',
          'shippingDestination': {
            '@type': 'DefinedRegion',
            'addressCountry': '001'
          },
          'deliveryTime': {
            '@type': 'ShippingDeliveryTime',
            'handlingTime': {
              '@type': 'QuantitativeValue',
              'minValue': 5,
              'maxValue': 21,
              'unitCode': 'd'
            },
            'transitTime': {
              '@type': 'QuantitativeValue',
              'minValue': 5,
              'maxValue': 21,
              'unitCode': 'd'
            }
          }
        }
      })
    }
  ]
})

/**
* GTM
* @event view_item
*/
const gtmProductInfo = {
  item_id: productData.value?.product?.productpartnumber,
  item_name: productData.value?.product?.producttitle,
  item_brand: confMain.siteName,
  item_category: '',
  price: productData.value?.product?.pre_populated_unit_price,
  quantity: 1,
  item_list_name: 'Product Page'
}

// View Product
trackEventQueue.value.push({ ecommerce: null })
trackEventQueue.value.push({
  event: 'view_item',
  ecommerce: {
    items: [gtmProductInfo]
  }
})
</script>

<style lang="scss" scoped>
</style>
